import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { useState, Suspense, lazy } from "react";
import DelayedSpinner from "./utils/Spinner";
import ErrorPage from "./utils/ErrorPage";
import ErrorPageWrapper from "./components/ErrorPageWrapper";
import PaymentConfirmation from "./pages/checkin/steps/PaymentConfirmation";
import DashboardPageContainer from "./components/DashboardPageContainer";
import { HotelProvider } from "./contexts/HotelContext";

// Imports dynamiques des composants de navigation
const WebsiteNavbar = lazy(() => import("./nav/WebsiteNavbar"));
const BookletNavbar = lazy(() => import("./nav/BookletNavbar"));
const DashboardNavbar = lazy(() => import("./nav/DashboardNavbar"));

// Imports dynamiques des pages du site
const Home = lazy(() => import("./pages/website/Home"));
const About = lazy(() => import("./pages/website/About"));
const Pricing = lazy(() => import("./pages/website/Pricing"));
const Contact = lazy(() => import("./pages/website/Contact"));
const CookiePolicy = lazy(() => import("./pages/website/CookiePolicy"));
const PrivacyPolicy = lazy(() => import("./pages/website/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/website/TermsOfService"));
const ThreeStarHotel = lazy(() =>
  import("./pages/website/pricing/ThreeStarHotel")
);
const FiveStarHotel = lazy(() =>
  import("./pages/website/pricing/FiveStarHotel")
);
const AirbnbAgency = lazy(() => import("./pages/website/pricing/AirbnbAgency"));

// Imports dynamiques des pages produit
const DigitalWelcomeBook = lazy(() =>
  import("./pages/website/solutions/DigitalWelcomeBook")
);
const Upsells = lazy(() => import("./pages/website/solutions/Upsells"));
const OnlineCheckin = lazy(() =>
  import("./pages/website/solutions/OnlineCheckin")
);
const ChatbotProduct = lazy(() =>
  import("./pages/website/solutions/ChatbotProduct")
);
const KycProduct = lazy(() => import("./pages/website/solutions/KycProduct"));

// Imports dynamiques des pages d'authentification
const Login = lazy(() => import("./pages/auth/Login"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const OnboardingFlow = lazy(() => import("./pages/auth/OnboardingFlow"));
const CreateHotelSetup = lazy(() => import("./pages/auth/CreateHotelSetup"));
const OAuthSuccess = lazy(() => import("./pages/auth/OAuthSuccess"));

// Imports dynamiques des pages du dashboard
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const ManageCompany = lazy(() => import("./pages/dashboard/ManageCompany"));
const Reservations = lazy(() => import("./pages/dashboard/Reservations"));
const EditWelcomeBook = lazy(() => import("./pages/dashboard/EditWelcomeBook"));
const Preview = lazy(() => import("./pages/dashboard/Preview"));
const MyQRCode = lazy(() => import("./pages/dashboard/MyQRCode"));
const PopUpDemo = lazy(() => import("./pages/dashboard/PopUpDemo"));
const Parametres = lazy(() => import("./pages/dashboard/Parametres"));
const Payments = lazy(() => import("./pages/dashboard/Payments"));
const ChatManager = lazy(() => import("./pages/dashboard/ChatManager"));
const ReservationDetails = lazy(() =>
  import("./components/reservations/ReservationDetails")
);
const CheckInForm = lazy(() => import("./pages/checkin/CheckInForm"));
const ManageUnits = lazy(() => import("./pages/dashboard/ManageUnits"));
const TaskManager = lazy(() => import("./pages/dashboard/TaskManager"));
const StaffMobileInterface = lazy(() => import("./components/taskManager/StaffMobileInterface"));

// Imports dynamiques des pages de paiement
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const StripePaymentForm = lazy(() => import("./components/payments/StripePaymentForm"));
const PaymentRedirect = lazy(() => import("./components/payments/PaymentRedirect"));

// Imports dynamiques des autres composants
const Information = lazy(() => import("./pages/Information"));
const Taxi = lazy(() => import("./pages/Taxi"));
const ActivityDetails = lazy(() => import("./components/ActivityDetails"));
const PaymentSuccess = lazy(() => import("./components/payments/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./components/payments/PaymentCancel"));
const PaymentPending = lazy(() => import("./components/payments/PaymentPending"));
const PreviewChatbot = lazy(() => import("./components/preview/PreviewChatbot"));
const UnitWelcomeBook = lazy(() => import("./components/UnitWelcomeBook"));

const Services = lazy(() => import("./components/editwelcomeBook/Services"));
const GuestbookRedirect = lazy(() => import("./pages/GuestbookRedirect"));
const ServiceDetails = lazy(() => import("./components/guestbook/ServiceDetails"));

// Ancienne ErrorPage inline supprimée au profit d'un composant générique utils/ErrorPage

const ProtectedDashboardRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");
  const location = useLocation();

  if (!token || !user) {
    console.log("No token or user found, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Ne pas rediriger vers le dashboard pendant le flux d'onboarding
  const isOnboardingFlow = location.pathname === "/onboarding";

  // Si l'utilisateur a une companyId, il peut accéder au dashboard
  if (user.companyId && !isOnboardingFlow) return children;

  // Sinon, rediriger vers l'onboarding
  if (!isOnboardingFlow) {
    console.log("User has no companyId, redirecting to onboarding");
    return <Navigate to="/onboarding" />;
  }

  return children;
};

const LoadingFallback = () => <DelayedSpinner minDelay={50} />;

const AppContent = () => {
  const location = useLocation();
  const [lang, setLang] = useState("en");
  const apartmentId = localStorage.getItem("currentApartmentId");
  const isApartmentIdValid = apartmentId && !isNaN(apartmentId);

  // Détermine si l'URL contient un apartmentId
  const isBookletRoute =
    /\/apartment\/\d+|\/services\/\d+|\/activities\/\d+|\/taxi\/\d+|\/chatbot\/\d+/.test(
      location.pathname
    );
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  const isPublicHotelRoute =
    location.pathname.startsWith("/:slug") ||
    (!location.pathname.startsWith("/dashboard") &&
      !location.pathname.startsWith("/about") &&
      !location.pathname.startsWith("/pricing") &&
      !location.pathname.startsWith("/contact") &&
      !location.pathname.startsWith("/signup") &&
      !location.pathname.startsWith("/login") &&
      !location.pathname.startsWith("/solutions") &&
      location.pathname !== "/");

  const isAuthRoute =
    location.pathname === "/signup" || location.pathname === "/login";
  const isCheckInRoute = location.pathname.startsWith("/checkin");

  return (
    <Suspense fallback={<LoadingFallback />}>
      {!isPublicHotelRoute &&
        !isAuthRoute &&
        !isCheckInRoute &&
        (isDashboardRoute ? (
          <DashboardNavbar />
        ) : isBookletRoute ? (
          <BookletNavbar />
        ) : (
          <WebsiteNavbar />
        ))}
      <Routes>
        {/* Routes publiques */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing/three-star" element={<ThreeStarHotel />} />
        <Route path="/pricing/five-star" element={<FiveStarHotel />} />
        <Route path="/pricing/airbnb" element={<AirbnbAgency />} />

        {/* Routes des pages produit */}
        <Route
          path="/solutions/digital-welcome"
          element={<DigitalWelcomeBook />}
        />
        <Route path="/solutions/upsells" element={<Upsells />} />
        <Route path="/solutions/online-checkin" element={<OnlineCheckin />} />
        <Route path="/solutions/chatbot" element={<ChatbotProduct />} />
        <Route path="/solutions/kyc" element={<KycProduct />} />

        <Route path="/contact" element={<Contact />} />

        {/* Legal pages */}
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/oauth-success" element={<OAuthSuccess />} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/create-hotel-setup" element={<CreateHotelSetup />} />

        {/* Routes protégées du dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Dashboard />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/company"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ManageCompany />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ManageUnits />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/units/upgrade"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ManageUnits />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/digitalbook"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <EditWelcomeBook />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/reservations"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Reservations />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/payments"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Payments />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/taskmanager"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <TaskManager />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/chatbot"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ChatManager />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/reservations/:id"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <ReservationDetails />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/preview"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Preview />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/qrcode"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <MyQRCode />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/pop-up"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <PopUpDemo />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />
        <Route
          path="/dashboard/settings"
          element={
            <ProtectedDashboardRoute>
              <DashboardPageContainer>
                <Parametres />
              </DashboardPageContainer>
            </ProtectedDashboardRoute>
          }
        />

        {/* Route pour le check-in - DOIT être avant les routes génériques */}
        <Route path="/checkin/:id" element={<CheckInForm />} />
        {/* Route pour le check-in avec companyName */}
        <Route path="/:companyName/checkin/:id" element={<CheckInForm />} />

        {/* Routes pour le site de vente */}
        <Route path="/apartment/:apartmentId" element={<Information />} />
        {/* <Route path="/services/:apartmentId" element={<Services />} /> */}
        <Route path="/taxi/:apartmentId" element={<Taxi />} />
        <Route path="/chatbot/:apartmentId" element={<PreviewChatbot />} />
        <Route path="/details/:apartmentId/:id" element={<ActivityDetails />} />
        <Route path="/payment-success" element={<PaymentSuccess themeColor="teal.500" />} />
        <Route path="/payment-cancel" element={<PaymentCancel themeColor="teal.500" />} />
        <Route path="/payment-pending" element={<PaymentPending themeColor="teal.500" />} />
        <Route path="/payment-redirect" element={<PaymentRedirect />} />

        {/* Route pour la confirmation de paiement */}
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />

        {/* Route pour la page de paiement */}
        <Route path="/payment/:reservationId/:serviceType" element={<PaymentPage />} />
        {/* Route pour la page de paiement depuis le check-in */}
        <Route path="/payment/checkin/:reservationId/:serviceType" element={<PaymentPage />} />

        {/* Page d'erreur */}
        <Route
          path="/error"
          element={<ErrorPage />}
        />

        {/* Catch-all 404 - Spécialement pour les guests */}
        <Route path="*" element={<ErrorPageWrapper code={404} />} />

        {/* Route pour la redirection du guestbook */}
        <Route path="/guestbook-redirect/:slug" element={<GuestbookRedirect />} />
        {/* Route pour la redirection du guestbook avec companyName */}
        <Route path="/:companyName/guestbook-redirect/:unitId" element={<GuestbookRedirect />} />
        {/* Route pour le guestbook */}
        <Route path="/guestbook/:token" element={<UnitWelcomeBook />} />
        {/* Route pour le guestbook avec companyName */}
        <Route path="/:companyName/guestbook/:token" element={<UnitWelcomeBook />} />

        {/* Route pour les détails des services */}
        <Route path="/service/:hotelSlug/:serviceSlug" element={<ServiceDetails />} />

        {/* Interface mobile pour les utilisateurs */}
        <Route path="/staff/:email" element={<StaffMobileInterface />} />

        {/* Route pour une reservation spécifique - DOIT être avant /:slug */}
        <Route path="/:slug/:token" element={<UnitWelcomeBook />} />
        {/* Route pour une unité spécifique */}
        <Route path="/:slug/:unitId" element={<UnitWelcomeBook />} />
        {/* Route pour l'hôtel public - DOIT être en dernier */}
        <Route path="/:slug" element={<UnitWelcomeBook />} />
      </Routes>
    </Suspense>
  );
};

const AppRouter = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <HotelProvider>
        <Suspense fallback={<LoadingFallback />}>
          <AppContent />
        </Suspense>
      </HotelProvider>
    </Router>
  );
};

export default AppRouter;
